export default function notificationTray(config = {}) {
    return {
        tasks: config.tasks ?? [],
        notifications: config.notifications ?? [],
        pollInterval: config.pollInterval ?? 5000,
        idlePollInterval: config.idlePollInterval ?? 60000,
        indexUrl: config.indexUrl ?? null,
        csrfToken: config.csrfToken ?? document.head.querySelector('meta[name=csrf-token]')?.content,
        open: false,
        errors: {},
        _timer: null,
        _timerInterval: null,
        _inFlight: false,

        init() {
            document.addEventListener('visibilitychange', () => {
                if (!document.hidden) this.refresh();
            });
            window.addEventListener('focus', () => this.refresh());
            this.syncPolling();
        },

        get hasActive() {
            return this.tasks.length > 0;
        },

        get badge() {
            return this.notifications.length;
        },

        get needsAttention() {
            return this.notifications.some(notification => ['error', 'warning'].includes(notification.level));
        },

        task(id) {
            return this.tasks.find(task => task.id === id) ?? null;
        },

        taskForSubject(subjectId) {
            return this.tasks.find(task => task.subject_id === subjectId) ?? null;
        },

        addTask(task) {
            const index = this.tasks.findIndex(existing => existing.id === task.id);
            if (index === -1) {
                this.tasks.push(task);
            } else {
                this.tasks.splice(index, 1, task);
            }
            this.syncPolling();
        },

        toggle() {
            this.open = !this.open;
            if (this.open) this.refresh();
        },

        async refresh() {
            if (!this.indexUrl || this._inFlight) return;

            this._inFlight = true;
            try {
                const response = await fetch(this.indexUrl, {headers: {Accept: 'application/json'}});
                if (!response.ok) return;
                this.apply(await response.json());
            } catch (error) {
                console.error('Notification tray:', error);
            } finally {
                this._inFlight = false;
            }
        },

        apply(data) {
            const previous = this.tasks;
            this.tasks = data.tasks ?? [];
            this.notifications = data.notifications ?? [];

            previous
                .filter(old => !this.tasks.some(task => task.id === old.id))
                .forEach(old => this.finished(old));

            this.tasks
                .filter(task => {
                    const old = previous.find(item => item.id === task.id);
                    return old && (old.done !== task.done || old.failed !== task.failed || old.status !== task.status);
                })
                .forEach(task => window.dispatchEvent(new CustomEvent('background-task-progress', {detail: {task}})));

            this.syncPolling();
        },

        finished(task) {
            const notification = this.notifications.find(item => item.extra?.task_id === task.id) ?? null;

            window.dispatchEvent(new CustomEvent('background-task-finished', {
                detail: {task, notification},
            }));
        },

        syncPolling() {
            const interval = this.hasActive ? this.pollInterval : this.idlePollInterval;

            if (this._timerInterval === interval) return;

            clearInterval(this._timer);
            this._timerInterval = interval;
            this._timer = setInterval(() => {
                if (!document.hidden) this.refresh();
            }, interval);
        },

        async markRead(notification) {
            this.notifications = this.notifications.filter(item => item.id !== notification.id);
            await this.post(notification.read_url);
        },

        async restart(url) {
            const response = await this.post(url);
            let message = null;

            if (!response?.ok) {
                const body = response ? await response.json().catch(() => ({})) : {};
                message = body.message ?? 'Kunde inte starta om jobbet.';
            }

            this.errors = {...this.errors, [url]: message};
            await this.refresh();

            return message;
        },

        async post(url) {
            if (!url) return null;
            try {
                return await fetch(url, {
                    method: 'POST',
                    headers: {
                        Accept: 'application/json',
                        'Content-Type': 'application/json',
                        'X-CSRF-TOKEN': this.csrfToken,
                    },
                });
            } catch (error) {
                console.error('Notification tray:', error);
                return null;
            }
        },
    };
}
